<template>
  <fragment>
    <AppBody
      title="Relatórios Analíticos"
      subtitle="Financeiro por Produtor"
      :actions="actions"
      :primaryLoading="exportCSVLoading"
      :secondaryLoading="exportPDFLoading"
      disableDivider
    >
      <CardModule untitle v-if="isAdmin">
        <ExpansionPanelFilters>
          <v-form @submit.prevent="filterFinancialReport" class="search-forms">
            <v-row>
              <v-col cols="12" md="3" sm="4">
                <BaseTextField
                  v-model="filter.pesquisar"
                  :placeholder="$t('reports.filter')"
                  @input="handleSearch"
                  append-icon="mdi-magnify"
                  single-line
                  hide-details
                  clearable
                ></BaseTextField>
              </v-col>
              <v-col cols="12" md="3" sm="4">
                <BaseAutocomplete
                  :label="$t('reports.sales-by-event.events')"
                  :placeholder="$t('reports.sales-by-event.select-multiple')"
                  clearable
                  deletable-chips
                  chips
                  multiple
                  :loading="financialReportLoading"
                  :items="allEvents"
                  v-model="filter.events"
                  item-text="descricao"
                  item-value="idproduto"
                  @click:clear="filter.events = null"
                >
                </BaseAutocomplete>
              </v-col>
              <v-col cols="12" md="3" sm="4" v-if="isAdmin">
                <BaseSelect
                  v-model="filter.regional"
                  :label="$t('reports.general-sales.regional')"
                  :placeholder="$t('reports.general-sales.select-regional')"
                  :items="regionais"
                  loader-height="5"
                  item-text="nome"
                  item-value="idregional"
                  data-cy="regional"
                  clearable
                  @click:clear="filter.regional = null"
                />
              </v-col>
              <v-col cols="12" md="3" sm="4">
                <BaseSelect
                  :label="$t('reports.channel-sales.input')"
                  :placeholder="$t('reports.channel-sales.select')"
                  :items="channelSales"
                  data-cy="canal-venda"
                  v-model="filter.channel"
                  item-text="value"
                  item-value="key"
                  clearable
                  @click:clear="filter.channel = null"
                />
              </v-col>
              <v-col cols="12" md="3" sm="4">
                <BaseSelect
                  v-model="filter.payment"
                  :label="$t('reports.payment-method.input')"
                  :placeholder="$t('reports.payment-method.select')"
                  :items="paymentMethods"
                  item-text="key"
                  item-value="value"
                  clearable
                  @click:clear="filter.payment = null"
                />
              </v-col>

              <v-col cols="12" md="3" sm="4">
                <date-time-picker
                  :label="$t('reports.financial-producer.initial-date-order')"
                  @formatted-date="handleInitDateTimeOrders"
                ></date-time-picker>
              </v-col>

              <v-col cols="12" md="3" sm="4">
                <date-time-picker
                  :label="$t('reports.financial-producer.final-date-order')"
                  @formatted-date="handleEndDateTimeOrders"
                ></date-time-picker>
              </v-col>

              <v-col cols="12" md="3" sm="4">
                <date-time-picker
                  :label="
                    $t('reports.financial-producer.initial-date-liberation')
                  "
                  @formatted-date="handleInitDateTimeRelease"
                ></date-time-picker>
              </v-col>

              <v-col cols="12" md="3" sm="4">
                <date-time-picker
                  :label="
                    $t('reports.financial-producer.final-date-liberation')
                  "
                  @formatted-date="handleEndDateTimeRelease"
                ></date-time-picker>
              </v-col>

              <v-col cols="12" md="3" sm="4">
                <v-checkbox
                  v-model="filter.multipleProducers"
                  :label="$t('reports.financial-producer.multiple-producers')"
                  class="mt-0"
                  hide-details
                ></v-checkbox>
              </v-col>

              <v-col class="d-flex justify-end">
                <BaseButton
                  variant="filter"
                  type="submit"
                  :loading="financialReportLoading"
                >
                  {{ $t("reports.filter") }}
                </BaseButton>
              </v-col>
            </v-row>
          </v-form>
        </ExpansionPanelFilters>

        <v-data-table
          class="rounded-lg"
          hide-details
          :no-data-text="$t('reports.no-info-found')"
          :loading-text="$t('reports.loading-info')"
          :loading="financialReportLoading"
          :headers="headerTableValues"
          :items="allFinancialReport.info"
          :items-per-page="10"
          :custom-sort="$relatorios_sinteticos.customSort"
          mobile-breakpoint="0"
          :header-props="{
            class: 'rounded-t-lg',
            color: $config.dados.corinputs,
          }"
          :footer-props="{
            class: 'rounded-b-lg',
            itemsPerPageOptions: [10, 20, 50],
            itemsPerPageText: $t('reports.financial-producer.items-per-page'),
          }"
        >
          <template v-slot:item.transactioncode="{ item }">
            {{ item.transactioncode || "-" }}
          </template>
          <template v-slot:item.idtransacao="{ item }">
            {{ item.idtransacao || "-" }}
          </template>
          <template v-slot:item.valor_pedido="{ item }">
            {{ item.valor_pedido | moeda }}
          </template>
          <template v-slot:item.dt_pedido="{ item }">
            {{ item.dt_pedido | dthr_pt_br }}
          </template>
          <template v-slot:item.dt_liberacao="{ item }">
            {{ item.dt_liberacao | dthr_pt_br }}
          </template>
          <template v-slot:item.valor_juros="{ item }">
            {{ item.valor_juros | moeda }}
          </template>
          <template v-slot:item.valor_cancelado="{ item }">
            {{ item.valor_cancelado | moeda }}
          </template>
          <template v-slot:item.valor_desconto="{ item }">
            {{ item.valor_desconto | moeda }}
          </template>
          <template v-slot:item.forma_pag="{ item }">
            {{ metodoPagamentoExibicao(item.forma_pag) }}
          </template>
          <template v-slot:item.valor_total="{ item }">
            {{ item.valor_total | moeda }}
          </template>
          <template v-slot:item.comissao_eleven="{ item }">
            {{ item.comissao_eleven | moeda }}
          </template>
          <template v-slot:item.comissao_parcela_eleven="{ item }">
            {{ item.comissao_parcela_eleven | moeda }}
          </template>
          <template v-slot:item.total_repasse_eleven="{ item }">
            {{ item.total_repasse_eleven | moeda }}
          </template>
          <template v-slot:item.comissao_pdv="{ item }">
            {{ item.comissao_pdv | moeda }}
          </template>
          <template v-slot:item.comissao_parcela_pdv="{ item }">
            {{ item.comissao_parcela_pdv | moeda }}
          </template>
          <template v-slot:item.total_repasse_pdv="{ item }">
            {{ formatCurrency(item.total_repasse_pdv) }}
          </template>
          <template v-slot:item.taxa_processamento="{ item }">
            {{ item.taxa_processamento | moeda }}
          </template>
          <template v-slot:item.taxa_conveniencia="{ item }">
            {{ item.taxa_conveniencia | moeda }}
          </template>
          <template v-slot:item.taxa_frete="{ item }">
            {{ item.taxa_frete | moeda }}
          </template>
          <template v-slot:item.taxa_pacote="{ item }">
            {{ item.taxa_pacote | moeda }}
          </template>
          <template v-slot:item.taxa_cortesia="{ item }">
            {{ item.taxa_cortesia | moeda }}
          </template>
          <template v-slot:item.taxa_gratuidade="{ item }">
            {{ item.taxa_gratuidade | moeda }}
          </template>

          <template v-slot:body.append>
            <tr class="text-end font-weight-bold total-row">
              <td class="text-start">Total</td>
              <td colspan="6"></td>
              <td v-if="!filter.channel"></td>
              <td v-for="total in totalValues" :key="total.name">
                {{ formatCurrency(total.value) }}
              </td>
            </tr>
          </template>
        </v-data-table>
      </CardModule>
    </AppBody>
  </fragment>
</template>
<script>
import dayjs from "dayjs";
import _ from "lodash";
import { formatCurrency } from "@/utils";

import AppBody from "@/layouts/components/AppBody.vue";
import CardModule from "@/components/cards/CardModule.vue";
import DateTimePicker from "@/components/forms/DateTimePicker.vue";
import BaseSelect from "@/components/forms/BaseSelect.vue";
import BaseTextField from "@/components/forms/BaseTextField.vue";
import BaseButton from "@/components/forms/BaseButton.vue";
import BaseAutocomplete from "@/components/forms/BaseAutocomplete.vue";
import ExpansionPanelFilters from "@/components/forms/ExpansionPanelFilters.vue";
import { mapActions, mapGetters, mapMutations } from "vuex";
export default {
  name: "FinancialAnalytical",
  metaInfo() {
    const title = this.$t("reports.financial-producer.title");
    return {
      title: `${title} | ElevenTicket`,
      meta: [
        {
          property: "og:title",
          content: `${title} | ElevenTicket`,
        },
      ],
    };
  },
  components: {
    AppBody,
    CardModule,
    DateTimePicker,
    BaseSelect,
    BaseTextField,
    BaseButton,
    BaseAutocomplete,
    ExpansionPanelFilters,
  },
  data() {
    return {
      formatCurrency: formatCurrency,
      actions: [
        {
          id: "CSV",
          name: "",
          icon: "mdi-microsoft-excel",
          action: () => this.exportCSV(),
        },
        {
          id: "PDF",
          name: "",
          icon: "mdi-file-pdf-box",
          action: () => this.exportPDF(),
        },
      ],
      filter: {
        pesquisar: "",
        events: null,
        produtos: [],
        regional: null,
        channel: null,
        payment: null,

        initDateTimeOrders: "",
        endDateTimeOrders: "",

        initDateTimeRelease: "",
        endDateTimeRelease: "",
      },
      dayjs: dayjs,
      date: null,
      tipo_pessoa: "LOJA",
    };
  },
  methods: {
    ...mapActions([
      "fetchFinancialReport",
      "fetchChannelSalesOptions",
      "fetchRegionais",
      "fetchPaymentMethodOptions",
      "fetchEvents",
      "exportFinancialReportPDF",
      "exportFinancialReportCSV",
    ]),
    ...mapMutations(["setFinancialReportLoading", "setReportsFilter"]),

    filterFinancialReport() {
      let filter = {};

      if (this.filter.initDateTimeOrders) {
        Object.assign(filter, {
          dthr_ini: this.filter.initDateTimeOrders,
        });
      }

      if (this.filter.endDateTimeOrders) {
        Object.assign(filter, {
          dthr_fim: this.filter.endDateTimeOrders,
        });
      }

      if (this.filter.initDateTimeRelease) {
        Object.assign(filter, {
          dthr_ini_lib: this.filter.initDateTimeRelease,
        });
      }

      if (this.filter.endDateTimeRelease) {
        Object.assign(filter, {
          dthr_fim_lib: this.filter.endDateTimeRelease,
        });
      }

      if (this.filter.events) {
        Object.assign(filter, {
          produtos: this.filter.events,
        });
      }

      Object.assign(filter, {
        pesquisar: this.filter.pesquisar,
        producerId: this.storeSequencial,
        tipo_pessoa: this.tipo_pessoa,
        idregional: this.filter.regional,
        canal_venda: this.filter.channel,
        forma_pagamento: this.filter.payment,
        multi_loja: this.filter.multipleProducers,
      });
      this.fetchFinancialReport(filter);
      this.setReportsFilter(filter);
    },
    exportCSV() {
      let filter = {
        producerId: this.storeSequencial,
        tipo_pessoa: this.tipo_pessoa,
        idregional: this.filter.regional,
        canal_venda: this.filter.channel,
        forma_pagamento: this.filter.payment,
      };

      if (this.filter.initDateTimeOrders) {
        Object.assign(filter, {
          dthr_ini: this.filter.initDateTimeOrders,
        });
      }

      if (this.filter.endDateTimeOrders) {
        Object.assign(filter, {
          dthr_fim: this.filter.endDateTimeOrders,
        });
      }

      if (this.filter.initDateTimeRelease) {
        Object.assign(filter, {
          dthr_ini_lib: this.filter.initDateTimeRelease,
        });
      }

      if (this.filter.endDateTimeRelease) {
        Object.assign(filter, {
          dthr_fim_lib: this.filter.endDateTimeRelease,
        });
      }

      if (this.filter.events) {
        Object.assign(filter, {
          produtos: this.filter.events,
        });
      }

      this.exportFinancialReportCSV(filter);
    },
    exportPDF() {
      let filter = {
        producerId: this.storeSequencial,
        tipo_pessoa: this.tipo_pessoa,
        idregional: this.filter.regional,
        canal_venda: this.filter.channel,
        forma_pagamento: this.filter.payment,
      };

      if (this.filter.initDateTimeOrders) {
        Object.assign(filter, {
          dthr_ini: this.filter.initDateTimeOrders,
        });
      }

      if (this.filter.endDateTimeOrders) {
        Object.assign(filter, {
          dthr_fim: this.filter.endDateTimeOrders,
        });
      }

      if (this.filter.initDateTimeRelease) {
        Object.assign(filter, {
          dthr_ini_lib: this.filter.initDateTimeRelease,
        });
      }

      if (this.filter.endDateTimeRelease) {
        Object.assign(filter, {
          dthr_fim_lib: this.filter.endDateTimeRelease,
        });
      }

      if (this.filter.events) {
        Object.assign(filter, {
          produtos: this.filter.events,
        });
      }

      this.exportFinancialReportPDF(filter);
    },
    handleSearch: _.debounce(function (event) {
      this.filter.pesquisar = event;
      this.filterFinancialReport();
    }, 500),
    handleInitDateTimeOrders(formatteddatetime) {
      this.filter.initDateTimeOrders = formatteddatetime;
    },
    handleEndDateTimeOrders(formatteddatetime) {
      this.filter.endDateTimeOrders = formatteddatetime;
    },
    handleInitDateTimeRelease(formatteddatetime) {
      this.filter.initDateTimeRelease = formatteddatetime;
    },
    handleEndDateTimeRelease(formatteddatetime) {
      this.filter.endDateTimeRelease = formatteddatetime;
    },
    metodoPagamentoExibicao(item) {
      if (item === null) {
        return "-";
      } else if (item === "QRCODE") {
        return "PIX";
      } else {
        return item;
      }
    },
  },

  computed: {
    ...mapGetters([
      "storeSequencial",
      "intervalDates",
      "allFinancialReport",
      "financialReportLoading",
      "isAdmin",
      "channelSales",
      "regionais",
      "paymentMethods",
      "allEvents",
      "reportsFilter",
      "exportCSVLoading",
      "exportPDFLoading",
    ]),
    headersPOSLoja() {
      return [
        {
          text: this.$t("reports.order"),
          align: "start",
          sortable: false,
          value: "pedido",
          width: 160,
        },
        {
          text: this.$t("reports.financial-producer.transaction-number-pos"),
          align: "start",
          sortable: true,
          value: "transactioncode",
          width: 220,
        },
        {
          text: this.$t("reports.payment-method.input"),
          align: "start",
          sortable: false,
          value: "forma_pag",
          width: 160,
        },
        {
          text: this.$t("reports.financial-producer.installments"),
          align: "end",
          value: "parcelas",
          width: 150,
        },
        {
          text: this.$t("reports.financial-producer.order-date-hour"),
          align: "start",
          value: "dt_pedido",
          width: 180,
        },
        {
          text: this.$t("reports.financial-producer.liberation-date-hour"),
          align: "start",
          value: "dt_liberacao",
          width: 180,
        },
        {
          text: this.$t("reports.financial-producer.released-installment"),
          align: "end",
          value: "parcelas_lib",
          width: 150,
        },
        {
          text: this.$t("reports.financial-producer.total-payment"),
          align: "end",
          value: "valor_total",
          width: 150,
        },
        {
          text: this.$t("reports.financial-producer.cancelled-value"),
          align: "end",
          value: "valor_cancelado",
          width: 150,
        },
        {
          text: this.$t("reports.financial-producer.discounts"),
          align: "end",
          value: "valor_desconto",
          width: 150,
        },
        {
          text: this.$t("reports.financial-producer.fees"),
          align: "end",
          value: "valor_juros",
          width: 150,
        },
        {
          text: this.$t("reports.financial-producer.processing-fee"),
          align: "end",
          value: "taxa_processamento",
          width: 180,
        },
        {
          text: this.$t("reports.financial-producer.convenience-fee"),
          align: "end",
          value: "taxa_conveniencia",
          width: 160,
        },
        {
          text: this.$t("reports.financial-producer.shipping-fee"),
          align: "end",
          value: "taxa_frete",
          width: 160,
        },
        {
          text: this.$t("reports.financial-producer.package-fee"),
          align: "end",
          value: "taxa_pacote",
          width: 130,
        },
        {
          text: this.$t("reports.financial-producer.courtesy-fee"),
          align: "end",
          value: "taxa_cortesia",
          width: 150,
        },
        {
          text: this.$t("reports.financial-producer.free-fee"),
          align: "end",
          value: "taxa_gratuidade",
          width: 150,
        },
        {
          text: this.$t("reports.financial-producer.eleven-comission"),
          align: "end",
          value: "comissao_eleven",
          width: 150,
        },
        {
          text: this.$t(
            "reports.financial-producer.eleven-installment-comission"
          ),
          align: "end",
          value: "comissao_parcela_pdv",
          width: 190,
        },
        {
          text: this.$t("reports.financial-producer.total-transfer"),
          align: "end",
          value: "total_repasse_pdv",
          width: 150,
        },
      ];
    },
    headersLojas() {
      return [
        {
          text: this.$t("reports.order"),
          align: "start",
          sortable: false,
          value: "pedido",
          width: 160,
        },
        {
          text: this.$t("reports.financial-producer.transaction-number-web"),
          align: "start",
          sortable: true,
          value: "idtransacao",
          width: 220,
        },
        {
          text: this.$t("reports.financial-producer.transaction-number-pos"),
          align: "start",
          sortable: true,
          value: "transactioncode",
          width: 220,
        },
        {
          text: this.$t("reports.payment-method.input"),
          align: "start",
          sortable: false,
          value: "forma_pag",
          width: 160,
        },
        {
          text: this.$t("reports.financial-producer.installments"),
          align: "end",
          value: "parcelas",
          width: 150,
        },
        {
          text: this.$t("reports.financial-producer.order-date-hour"),
          align: "start",
          value: "dt_pedido",
          width: 180,
        },
        {
          text: this.$t("reports.financial-producer.liberation-date-hour"),
          align: "start",
          value: "dt_liberacao",
          width: 180,
        },
        {
          text: this.$t("reports.financial-producer.released-installment"),
          align: "end",
          value: "parcelas_lib",
          width: 150,
        },
        {
          text: this.$t("reports.financial-producer.total-payment"),

          align: "end",
          value: "valor_total",
          width: 150,
        },
        {
          text: this.$t("reports.financial-producer.cancelled-value"),
          align: "end",
          value: "valor_cancelado",
          width: 150,
        },
        {
          text: this.$t("reports.financial-producer.discounts"),
          align: "end",
          value: "valor_desconto",
          width: 150,
        },
        {
          text: this.$t("reports.financial-producer.fees"),
          align: "end",
          value: "valor_juros",
          width: 150,
        },
        {
          text: this.$t("reports.financial-producer.processing-fee"),
          align: "end",
          value: "taxa_processamento",
          width: 180,
        },
        {
          text: this.$t("reports.financial-producer.convenience-fee"),
          align: "end",
          value: "taxa_conveniencia",
          width: 170,
        },
        {
          text: this.$t("reports.financial-producer.shipping-fee"),
          align: "end",
          value: "taxa_frete",
          width: 160,
        },
        {
          text: this.$t("reports.financial-producer.package-fee"),
          align: "end",
          value: "taxa_pacote",
          width: 150,
        },
        {
          text: this.$t("reports.financial-producer.courtesy-fee"),
          align: "end",
          value: "taxa_cortesia",
          width: 150,
        },
        {
          text: this.$t("reports.financial-producer.free-fee"),
          align: "end",
          value: "taxa_gratuidade",
          width: 150,
        },
        {
          text: this.$t("reports.financial-producer.eleven-comission"),
          align: "end",
          value: "comissao_eleven",
          width: 150,
        },
        {
          text: this.$t(
            "reports.financial-producer.eleven-installment-comission"
          ),
          align: "end",
          value: "comissao_parcela_eleven",
          width: 190,
        },
        {
          text: this.$t("reports.financial-producer.total-transfer"),
          align: "end",
          value: "total_repasse_eleven",
          width: 150,
        },
      ];
    },
    headersWEBLojas() {
      return [
        {
          text: this.$t("reports.order"),
          align: "start",
          sortable: false,
          value: "pedido",
          width: 150,
        },
        {
          text: this.$t("reports.financial-producer.transaction-number-web"),
          align: "start",
          sortable: true,
          value: "idtransacao",
          width: 200,
        },
        {
          text: this.$t("reports.payment-method.input"),
          align: "start",
          sortable: false,
          value: "forma_pag",
          width: 160,
        },
        {
          text: this.$t("reports.financial-producer.installments"),
          align: "end",
          value: "parcelas",
          width: 150,
        },
        {
          text: this.$t("reports.financial-producer.order-date-hour"),
          align: "start",
          value: "dt_pedido",
          width: 180,
        },
        {
          text: this.$t("reports.financial-producer.liberation-date-hour"),
          align: "start",
          value: "dt_liberacao",
          width: 180,
        },
        {
          text: this.$t("reports.financial-producer.released-installment"),
          align: "end",
          value: "parcelas_lib",
          width: 150,
        },
        {
          text: this.$t("reports.financial-producer.total-payment"),
          align: "end",
          value: "valor_total",
          width: 180,
        },
        {
          text: this.$t("reports.financial-producer.cancelled-value"),
          align: "end",
          value: "valor_cancelado",
          width: 150,
        },
        {
          text: this.$t("reports.financial-producer.discounts"),
          align: "end",
          value: "valor_desconto",
          width: 150,
        },
        {
          text: this.$t("reports.financial-producer.fees"),
          align: "end",
          value: "valor_juros",
          width: 150,
        },
        {
          text: this.$t("reports.financial-producer.processing-fee"),
          align: "end",
          value: "taxa_processamento",
          width: 180,
        },
        {
          text: this.$t("reports.financial-producer.convenience-fee"),
          align: "end",
          value: "taxa_conveniencia",
          width: 170,
        },
        {
          text: this.$t("reports.financial-producer.shipping-fee"),
          align: "end",
          value: "taxa_frete",
          width: 160,
        },
        {
          text: this.$t("reports.financial-producer.package-fee"),
          align: "end",
          value: "taxa_pacote",
          width: 150,
        },
        {
          text: this.$t("reports.financial-producer.courtesy-fee"),
          align: "end",
          value: "taxa_cortesia",
          width: 150,
        },
        {
          text: this.$t("reports.financial-producer.free-fee"),
          align: "end",
          value: "taxa_gratuidade",
          width: 150,
        },
        {
          text: this.$t("reports.financial-producer.eleven-comission"),
          align: "end",
          value: "comissao_eleven",
          width: 150,
        },
        {
          text: this.$t(
            "reports.financial-producer.eleven-installment-comission"
          ),
          align: "end",
          value: "comissao_parcela_eleven",
          width: 190,
        },
        {
          text: this.$t("reports.financial-producer.total-transfer"),
          align: "end",
          value: "total_repasse_eleven",
          width: 150,
        },
      ];
    },
    headerTableValues() {
      switch (this.filter.channel) {
        case "PDV_POS":
          return this.headersPOSLoja;
        case "PDV_QRCODE":
        case "ECOMMERCE":
        case "MARKETPLACE":
          return this.headersWEBLojas;
        default:
          return this.headersLojas;
      }
    },
    isPOS() {
      return this.filter.channel === "PDV_POS";
    },
    totalValues() {
      return this.allFinancialReport.totals.flatMap(item => {
        return [
          {
            name: "valor_total_pagamento",
            value: item.valor_total_pagamento,
          },
          {
            name: "valor_cancelado",
            value: item.valor_cancelado,
          },
          {
            name: "valor_desconto",
            value: item.valor_desconto,
          },
          {
            name: "valor_total_juros",
            value: item.valor_total_juros,
          },
          {
            name: "valor_total_taxa_processamento",
            value: item.valor_total_taxa_processamento,
          },
          {
            name: "valor_total_taxa_conveniencia",
            value: item.valor_total_taxa_conveniencia,
          },
          {
            name: "valor_total_taxa_frete",
            value: item.valor_total_taxa_frete,
          },
          {
            name: "valor_total_taxa_pacote",
            value: item.valor_total_taxa_pacote,
          },
          {
            name: "valor_total_taxa_cortesia",
            value: item.valor_total_taxa_cortesia,
          },
          {
            name: "valor_total_taxa_gratuidade",
            value: item.valor_total_taxa_gratuidade,
          },
          {
            name: "valor_total_comissao",
            value: this.isPOS
              ? item.valor_total_comissao_pdv
              : item.valor_total_comissao_eleven,
          },
          {
            name: "valor_total_parc_liberado",
            value: this.isPOS
              ? item.valor_total_parc_liberado_pdv
              : item.valor_total_parc_liberado_eleven,
          },
          {
            name: "valor_total_repasse",
            value: this.isPOS
              ? item.valor_total_repasse_pdv
              : item.valor_total_repasse_eleven,
          },
        ];
      });
    },
  },
  watch: {
    menu(val) {
      val && setTimeout(() => (this.activePickerOrders = "MONTH"));
    },
    menu2(val) {
      val && setTimeout(() => (this.activePickerOrders2 = "MONTH"));
    },
    menu3(val) {
      val && setTimeout(() => (this.activePickerRelease = "MONTH"));
    },
    menu4(val) {
      val && setTimeout(() => (this.activePickerRelease2 = "MONTH"));
    },
  },
  mounted() {
    this.filterFinancialReport();
    this.fetchPaymentMethodOptions();
    this.fetchChannelSalesOptions();
    this.fetchRegionais();
    if (this.storeSequencial) {
      this.fetchEvents({
        producerId: this.storeSequencial,
      });
    }
  },
  destroyed() {
    this.filter = {
      pesquisar: "",
      regional: "",
      channel: "",
      payment: "",
      initDateTimeOrders: "",
      endDateTimeOrders: "",
      initDateTimeRelease: "",
      endDateTimeRelease: "",
      events: [],
      multipleProducers: false,
    };
  },
};
</script>
<style scoped></style>
